import React from 'react';

import { formatMoney, toMoney } from '@betterment/core';
import { useFlowState } from '@betterment/js-runtime';
import { Banner, Box, ContentLayoutF, H2 } from '@betterment/samba-react';
import { FormikValidateAfterSubmit, useFeatureFlag } from '@betterment/shared-components';
import { FieldArray, Form } from 'formik';
import * as Yup from 'yup';

import { BillingTierTypeBox } from './BillingTierTypeBox';
import { TieredBillingTable } from './Table';

import { parseFloatFromNumberInputMask } from '../../../../utils/input-helpers';
import FlowStepLayout from '../../../BettermentForAdvisors/FlowStepLayout';
import { makeUpsertBillingPlanInput } from '../api';
import { MAX_ASSET_BASED_FEE_RATE, MAX_FIXED_FEE_AMOUNT, MAX_TIERS } from '../constants';
import { BillingPlanFlowValues } from '../data';
import FeeSummaryBox from '../fee-summary-box/FeeSummaryBox';
import { useSubmitHandler } from '../hooks/useSubmitHandler';
import { PlatformFeeBox, platformFeeNettingSchema } from '../platform-fee-box/PlatformFeeBox';
import { getFlowTitle } from '../utils';

const TierSchema = Yup.object().shape({
  tierEnd: Yup.number()
    .transform((_, originalValue: string) => {
      const parsedValue = parseFloatFromNumberInputMask(originalValue);
      return Number.isNaN(parsedValue) ? null : parsedValue;
    })
    .positive('Must be greater than 0')
    .required('Ending balance is required'),
  firmFee: Yup.object().shape({
    feeConfigType: Yup.string<'assetBasedConfiguration' | 'fixedConfiguration'>().oneOf(
      ['assetBasedConfiguration', 'fixedConfiguration'],
      'Fee configuration type is required'
    ),
    feeValue: Yup.number().when('feeConfigType', {
      is: 'fixedConfiguration',
      then: () =>
        Yup.number()
          .transform((_, originalValue: string) => {
            const parsedValue = parseFloatFromNumberInputMask(originalValue);
            return Number.isNaN(parsedValue) ? null : parsedValue;
          })
          .max(MAX_FIXED_FEE_AMOUNT, `${formatMoney(toMoney(MAX_FIXED_FEE_AMOUNT * 100), 'defaultDollar')} maximum`)
          .required('Fee rate is required'),
      otherwise: () =>
        Yup.number()
          .transform((_, originalValue: string) => {
            const parsedValue = parseFloatFromNumberInputMask(originalValue);
            return Number.isNaN(parsedValue) ? null : parsedValue;
          })
          .integer('Must be an integer')
          .min(0, '0 bps minimum')
          .max(MAX_ASSET_BASED_FEE_RATE, `${MAX_ASSET_BASED_FEE_RATE} bps maximum`)
          .required('Fee rate is required'),
    }),
  }),
});

const TiersSchema = Yup.object().shape({
  tiers: Yup.array().of(TierSchema).required().min(1).max(MAX_TIERS),
  lastTier: Yup.object().shape({
    firmFee: Yup.object().shape({
      feeConfigType: Yup.string()
        .oneOf(['assetBasedConfiguration'], 'Fee configuration type must be asset-based')
        .required('Fee configuration type is required'),
      feeValue: Yup.number()
        .transform((_, originalValue: string) => {
          const parsedValue = parseFloatFromNumberInputMask(originalValue);
          return Number.isNaN(parsedValue) ? null : parsedValue;
        })
        .integer('Must be an integer')
        .min(0, '$0.00 minimum')
        .max(MAX_ASSET_BASED_FEE_RATE, `${MAX_ASSET_BASED_FEE_RATE} bps maximum`)
        .required('Fee rate is required'),
    }),
  }),
});

export const TieredBillingConfigurationStep: React.FC = () => {
  const {
    flowState: { values: flowValues },
  } = useFlowState<BillingPlanFlowValues>();
  const { submitHandler, errorMessage } = useSubmitHandler();
  const platformFeeTiers = flowValues.feeConfiguration?.platformFeeTiers || [];
  const validationSchema = TiersSchema.concat(platformFeeNettingSchema(flowValues.platformFee));

  const { enabled: isCliffBasedBillingEnabled, loading: isLoading } = useFeatureFlag({
    splitName: 'retail.b4a_cliff_based_billing_q1_2026_enabled',
    context: 'BillingPlanFlow',
  });

  return (
    <FlowStepLayout exitToPath={flowValues.exitToPath} title={getFlowTitle(flowValues, 2)}>
      <Box css={{ width: 'container.max', display: 'flex', flexDirection: 'column', justifySelf: 'center' }}>
        <H2 css={{ pb: 'space.400' }}>Set tiered billing plan.</H2>
        {errorMessage && (
          <Banner variant="critical" css={{ mt: 'space.400' }}>
            <Banner.Content>{errorMessage}</Banner.Content>
          </Banner>
        )}
        <FormikValidateAfterSubmit
          initialValues={{
            // @ts-expect-error TS18048 violates strict null checks
            tiers: flowValues.tiers.slice(0, -1),
            // @ts-expect-error TS18048 violates strict null checks
            lastTier: flowValues.tiers[flowValues.tiers.length - 1],
            tieredFeeCalculationMethod: flowValues.tieredFeeCalculationMethod,
            platformFeePaidBy: flowValues.platformFeePaidBy,
            sharedPlatformFeeClientPortionBps: flowValues.sharedPlatformFeeClientPortionBps,
          }}
          onSubmit={async values => {
            await submitHandler({
              ...flowValues,
              tiers: values.tiers.concat([values.lastTier]),
              tieredFeeCalculationMethod: values.tieredFeeCalculationMethod,
              platformFeePaidBy: values.platformFeePaidBy,
              sharedPlatformFeeClientPortionBps: values.sharedPlatformFeeClientPortionBps,
            });
          }}
          validationSchema={validationSchema}
        >
          {({ values }) => (
            <Form>
              <ContentLayoutF>
                <ContentLayoutF.SlotContainer>
                  <ContentLayoutF.Slot1>
                    <FieldArray
                      name="tiers"
                      render={arrayHelpers => (
                        <TieredBillingTable
                          tiers={values.tiers}
                          addTier={(...args) => arrayHelpers.push(...args)}
                          removeTier={(...args) => arrayHelpers.remove(...args)}
                        />
                      )}
                    />
                    {isCliffBasedBillingEnabled && !isLoading && (
                      <Box css={{ mt: 'space.400' }}>
                        <BillingTierTypeBox />
                      </Box>
                    )}
                    <Box css={{ mt: 'space.400' }}>
                      <PlatformFeeBox platformFee={flowValues.platformFee} tiers={platformFeeTiers} />
                    </Box>
                  </ContentLayoutF.Slot1>
                  <ContentLayoutF.Slot2>
                    <Box css={{ position: 'sticky', top: '20px' }}>
                      <FeeSummaryBox
                        billingPlan={makeUpsertBillingPlanInput({
                          ...flowValues,
                          tiers: values.tiers.concat([values.lastTier]),
                        })}
                        platformFee={flowValues.platformFee}
                        numberOfClients={flowValues.numberOfClients}
                      />
                    </Box>
                  </ContentLayoutF.Slot2>
                </ContentLayoutF.SlotContainer>
              </ContentLayoutF>
            </Form>
          )}
        </FormikValidateAfterSubmit>
      </Box>
    </FlowStepLayout>
  );
};
